/**
 * useSiteContent — read editable copy by key with a guaranteed default.
 *
 * Contract:
 *  - returns `t(key)` which resolves stored override → code default → "".
 *  - NEVER renders a raw key string or a blank where a default exists.
 *  - renders defaults immediately on first paint (no loading flash), then
 *    swaps in stored overrides when they arrive.
 */
import { useCallback, useEffect, useState } from "react";
import { getSectionContent, DEFAULT_LOCALE } from "@/services/site-content";

export function useSiteContent(
  section: string,
  defaults: Record<string, string>,
  locale: string = DEFAULT_LOCALE
) {
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await getSectionContent(section, locale);
      if (!cancelled) {
        setOverrides(data);
        setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [section, locale]);

  const t = useCallback(
    (key: string): string => {
      const stored = overrides[key];
      if (typeof stored === "string" && stored.trim() !== "") return stored;
      const fallback = defaults[key];
      return typeof fallback === "string" ? fallback : "";
    },
    [overrides, defaults]
  );

  return { t, loaded };
}
